'use client';

import { formatMoney } from '@/src/lib/money';
import type { Cart, CartLine } from '@/src/lib/hospitality/orderWire';
import type { Orderability } from '@/src/lib/hospitality/wire';
import { addDecimals, compareDecimals } from '@/src/lib/hospitality/decimal';
import { cartTree } from './model';

/**
 * The cart, as the backend has it.
 *
 * <p>There is no client-side cart to render: `Cart`/`CartLine` ARE the cart,
 * and every figure below — line total, subtotal, tax, total — is the one the
 * pricing engine returned. Nothing here re-derives a total from unit prices; the
 * only arithmetic is a quantity step, and it goes through the decimal helpers.
 *
 * <p>Two rules are visible in the markup. `taxInclusive` is read PER LINE and
 * decides whether "+ tax" appears next to that price — a restaurant menu is not
 * inclusive because it is a restaurant. And a line the cut-off re-check has
 * flagged is rendered IN PLACE, marked, with its reason and a remove button:
 * never hidden, never silently dropped.
 */

export interface CartPanelProps {
  cart: Cart;
  currency: string;
  locale: string;
  t: (key: string, params?: Record<string, string | number>) => string;
  flagged: ReadonlyMap<number, Orderability>;
  onQuantity: (lineId: number, quantity: string) => void;
  onRemove: (lineId: number) => void;
  busy: boolean;
}

export function CartPanel({ cart, currency, locale, t, flagged, onQuantity, onRemove, busy }: CartPanelProps) {
  const money = (amount: string | null) =>
    amount ? (formatMoney({ amount, currency }, locale) ?? amount) : null;
  const nodes = cartTree(cart);

  if (nodes.length === 0) {
    return <p className="text-sm text-[var(--color-text-secondary)]">{t('hospitality.order.cartEmpty')}</p>;
  }

  return (
    <div>
      <ul className="divide-y">
        {nodes.map(({ line, modifiers }) => {
          const problem = flagged.get(line.id);
          return (
            <li
              key={line.id}
              className={problem ? 'py-3 bg-red-50 px-2' : 'py-3'}
              aria-invalid={problem ? true : undefined}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">{line.description}</p>
                  {modifiers.length > 0 ? (
                    <ul className="mt-1 space-y-0.5">
                      {modifiers.map((modifier) => (
                        <li key={modifier.id} className="text-xs text-[var(--color-text-secondary)]">
                          + {modifier.description}
                          {modifier.lineTotal && compareDecimals(modifier.lineTotal, '0') > 0
                            ? ` ${money(modifier.lineTotal) ?? ''}`
                            : ''}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                  {problem ? (
                    <p role="alert" className="mt-1 text-xs font-medium text-red-700">
                      {t(unorderableKey(problem))}
                    </p>
                  ) : null}
                </div>
                <div className="shrink-0 text-right">
                  <p className="text-sm">
                    {money(lineWithModifiers(line, modifiers))}
                    {line.taxInclusive ? null : (
                      <span className="ml-1 text-xs text-[var(--color-text-secondary)]">
                        {t('hospitality.order.plusTax')}
                      </span>
                    )}
                  </p>
                </div>
              </div>

              <div className="mt-2 flex items-center gap-2">
                <label className="sr-only" htmlFor={`qty-${line.id}`}>
                  {t('hospitality.order.quantityFor', { item: line.description })}
                </label>
                <input
                  id={`qty-${line.id}`}
                  type="number"
                  min={0}
                  step={1}
                  inputMode="numeric"
                  disabled={busy}
                  defaultValue={line.quantity ?? '1'}
                  onBlur={(event) => {
                    const next = event.target.value.trim();
                    if (next && next !== line.quantity) onQuantity(line.id, next);
                  }}
                  className="w-16 rounded border px-2 py-1 text-sm"
                />
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => onRemove(line.id)}
                  className="text-sm underline"
                >
                  {t('hospitality.order.remove')}
                </button>
              </div>
            </li>
          );
        })}
      </ul>

      <dl className="mt-4 space-y-1 border-t pt-3 text-sm">
        <Row label={t('hospitality.order.subtotal')} value={money(cart.subtotal)} />
        {cart.discountTotal && compareDecimals(cart.discountTotal, '0') > 0 ? (
          <Row label={t('hospitality.order.discount')} value={money(cart.discountTotal)} />
        ) : null}
        {cart.shippingTotal && compareDecimals(cart.shippingTotal, '0') > 0 ? (
          <Row label={t('hospitality.order.delivery')} value={money(cart.shippingTotal)} />
        ) : null}
        <Row label={t('hospitality.order.tax')} value={money(cart.taxTotal)} />
        <Row label={t('hospitality.order.total')} value={money(cart.total)} strong />
      </dl>
    </div>
  );
}

function Row({ label, value, strong }: { label: string; value: string | null; strong?: boolean }) {
  return (
    <div className={`flex justify-between ${strong ? 'font-semibold' : ''}`}>
      <dt>{label}</dt>
      <dd>{value ?? '—'}</dd>
    </div>
  );
}

/** An item's own total plus its modifiers'. Modifiers are separate `CartLine`
 *  rows with their own totals, so the figure a diner reads next to a dish is a
 *  sum — done in minor units, never with `+` on two floats. */
function lineWithModifiers(line: CartLine, modifiers: CartLine[]): string | null {
  if (!line.lineTotal && modifiers.length === 0) return null;
  return addDecimals([line.lineTotal, ...modifiers.map((modifier) => modifier.lineTotal)]);
}

/**
 * The reason string for an unorderable line.
 *
 * <p>`UnorderableCode` is a union with a string escape hatch, so a code this
 * build has not heard of is real and must still read as a reason rather than as
 * a missing translation key. Anything outside the known set falls back to the
 * neutral sentence.
 */
const KNOWN_CODES = new Set([
  'MENU_ITEM_86ED',
  'MENU_OUTSIDE_WINDOW',
  'MENU_PAST_CUTOFF',
  'MENU_ITEM_NOT_SELLABLE',
]);

function unorderableKey(result: Orderability): string {
  return result.code && KNOWN_CODES.has(result.code)
    ? `hospitality.order.unorderable.${result.code}`
    : 'hospitality.order.unorderable.UNKNOWN';
}
